import { useTranslation } from 'react-i18next';

import { esIdWidget } from '#components/mobile/inicio/modeloWidgets';
import { descripcionWidget } from '#components/mobile/inicio/widgets/registro';

import type { PasoAyuda, PreguntaAyuda } from './pasos';

function uno(selector: string): Element[] {
  const el = document.querySelector(selector);
  return el ? [el] : [];
}

/** Tarjetas de Inicio que se ven ahora (las vacías no cuentan). */
function tarjetas(): Element[] {
  return [
    ...document.querySelectorAll<HTMLElement>(
      '[data-testid="inicio-widgets"] > [data-widget-id]',
    ),
  ].filter(el => el.offsetHeight > 0);
}

function primeraTarjeta(): Element[] {
  return tarjetas().slice(0, 1);
}

/**
 * La ayuda de Inicio (maqueta aprobada por Vadik el 26-oct-2026). Inicio es
 * personalizable y cada persona tiene sus tarjetas: «Cuéntame esta
 * pantalla» explica la pantalla y no cada tarjeta; cada tarjeta la explica
 * «Sobre una de mis tarjetas» (con el mismo texto que el catálogo).
 */
export function usePreguntasInicio(): PreguntaAyuda[] {
  const { t } = useTranslation();

  const pantalla: PasoAyuda[] = [
    {
      id: 'hola',
      escena: 'normal',
      texto: `${t(
        'Hi! This is your Home: your shortcuts. Each card is a shortcut and you choose them.',
      )} ${t('I asked for a card for my coins, but apparently that is not finance.')}`,
      objetivo: () => uno('[data-testid="inicio-saludo"]'),
    },
    {
      id: 'ancla',
      escena: 'normal',
      radio: 20,
      texto: t(
        'The anchor is your backup. Tap it to choose where it is saved and how often. If it turns red, the last one was a long time ago.',
      ),
      objetivo: () => uno('[data-testid="ancla-copia"]'),
    },
    {
      id: 'tarjetas',
      escena: 'normal',
      radio: 6,
      texto: t(
        'Below go your cards: shortcuts to what you check most. Each one tells you something at a glance and its buttons take you where you need to go. Scroll to see them all.',
      ),
      objetivo: () => uno('[data-testid="inicio-widgets"]'),
    },
    {
      id: 'brujula',
      escena: 'normal',
      radio: 24,
      texto: t('In the compass you have Customize home and Add transaction.'),
      objetivo: () => uno('[data-testid="brujula-opciones"]'),
    },
    {
      id: 'a-tu-gusto',
      escena: 'normal',
      texto: t(
        'To change it, keep your finger on any card. I show you how in "How do I change my Home?".',
      ),
      objetivo: primeraTarjeta,
    },
    {
      id: 'listo',
      escena: 'normal',
      radio: 22,
      final: true,
      texto: t(
        'Done! Questions about a card? Tap the porthole and choose "About one of my cards".',
      ),
      objetivo: () => uno('[data-testid="ojo-de-buey"]'),
    },
  ];

  const comoCambio: PasoAyuda[] = [
    {
      id: 'mantener',
      escena: 'normal',
      texto: t('Keep your finger on any card for a moment…'),
      objetivo: primeraTarjeta,
    },
    {
      id: 'editar',
      escena: 'inicio-editar',
      radio: 6,
      texto: t(
        '…and they all sway: you are customizing. Behind them, two columns appear where the cards fit. If dragging is not your thing, tap one and you get Up, Down, Size and Remove.',
      ),
      objetivo: () => uno('[data-testid="inicio-widgets"]'),
    },
    {
      id: 'arrastrar',
      escena: 'inicio-editar',
      texto: t('Drag it wherever you want; the others make room.'),
      objetivo: primeraTarjeta,
    },
    {
      id: 'tamano',
      escena: 'inicio-tamano',
      texto: t(
        'Tap the brass handle in the corner and choose Small, Wide or Large. Each card only offers the sizes in which it reads well.',
      ),
      objetivo: () => uno('[data-testid="inicio-selector-tamano"]'),
    },
    {
      id: 'quitar',
      escena: 'inicio-editar',
      radio: 18,
      texto: t(
        'The ✕ takes it off your Home. Nothing is deleted: below you have Undo, and you can put it back whenever you want.',
      ),
      objetivo: () => uno('[data-testid="inicio-quitar"]'),
    },
    {
      id: 'anadir',
      escena: 'inicio-editar',
      texto: t('With "+ Add card" you choose from all the cards there are.'),
      objetivo: () => uno('[data-testid="inicio-anadir-tarjeta"]'),
    },
    {
      id: 'catalogo',
      escena: 'inicio-catalogo',
      radio: 24,
      texto: t(
        'Each one comes with what it tells you and the sizes it comes in. Tap Add and it appears on your Home.',
      ),
      objetivo: () => uno('[data-testid="inicio-catalogo"]'),
    },
    {
      id: 'hecho',
      escena: 'inicio-editar',
      radio: 14,
      final: true,
      texto: `${t('Tap Done and it stays saved.')} ${t(
        'Arrange them as you like. I arrange my coins by shine.',
      )}`,
      objetivo: () => uno('[data-testid="inicio-hecho"]'),
    },
  ];

  return [
    { id: 'pantalla', texto: t('Tell me about this screen'), pasos: pantalla },
    {
      id: 'tarjeta',
      texto: t('About one of my cards'),
      pasos: [],
      elegir: {
        objetivos: tarjetas,
        texto: t(
          'Tap the card you want me to tell you about. They carry a badge with "?".',
        ),
        explicar: el => {
          const id = (el as HTMLElement).dataset.widgetId;
          return esIdWidget(id) ? descripcionWidget(t, id) : '';
        },
      },
    },
    {
      id: 'cambiar',
      texto: t('How do I change my Home?'),
      pasos: comoCambio,
    },
  ];
}
